import React, { useCallback, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAdminOrderStore } from "../../store/useAdminOrderStore.ts";
import { getAdminOrders } from "../../services/adminOrderService";

import OrderCard from "../../components/Fullfilment/OrderCard";
import AdminOrderFilters from "../../components/Fullfilment/AdminOrderFilters";
import Pagination from "../../components/Pagination";
import AdminOrderTabs from "../../components/Fullfilment/AdminOrderTabs";
import AdminSearchBox from "../../components/Fullfilment/AdminSearchBox";
import AdminReviewViewModal from "../../components/Fullfilment/Rating/admin/AdminReviewViewModal";

const AdminOrdersPage = () => {
  const {
    activeTab,
    currentPage,
    pageSize,
    setCurrentPage,
    setFilter,
    getFilters,

    isReviewViewModalOpen,
    reviewEntity,
    reviewData,
    closeReviewModal,
  } = useAdminOrderStore();

  const filters = getFilters();

  const handleSearch = useCallback(
    (value) => {
      setFilter("search", value);
    },
    [setFilter]
  );

  const queryKey = useMemo(
    () => [
      "admin-orders",
      activeTab,
      currentPage,
      pageSize,
      filters.fulfillmentStatus,
      filters.dateFrom,
      filters.dateTo,
      filters.minAmount,
      filters.maxAmount,
      filters.shipmentStatus,
      filters.deliveryMethod,
      filters.search,
      filters.ordering,
    ],
    [
      activeTab,
      currentPage,
      pageSize,
      filters.fulfillmentStatus,
      filters.dateFrom,
      filters.dateTo,
      filters.minAmount,
      filters.maxAmount,
      filters.shipmentStatus,
      filters.deliveryMethod,
      filters.search,
      filters.ordering,
    ]
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getAdminOrders({
        tab: activeTab,
        currentPage,
        pageSize,
        ...filters,
      }),
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  });

  const ordersList = Array.isArray(data) ? data : data?.results || [];

  const totalCount =
    typeof data?.count === "number"
      ? data.count
      : Array.isArray(data)
        ? data.length
        : 0;

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0;

  let content;

  if (isLoading) {
    content = <SkeletonLoader count={5} />;
  } else if (error) {
    content = (
      <div className="text-center text-red-600 p-8 bg-white rounded-lg shadow">
        <p>خطا در دریافت سفارش‌ها: {error.message}</p>

        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          تلاش مجدد
        </button>
      </div>
    );
  } else if (ordersList.length === 0) {
    content = (
      <div className="text-center text-gray-500 p-12 bg-white rounded-lg shadow border-2 border-dashed">
        <p className="text-lg">هیچ سفارشی با این مشخصات یافت نشد.</p>
      </div>
    );
  } else {
    content = (
      <div className="space-y-4">
        {ordersList.map((order) => (
          <OrderCard key={order.id} order={order} isAdmin={true} />
        ))}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <header className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">
          پنل مدیریت سفارشات
        </h1>

        <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
          تعداد کل: {totalCount} مورد
        </div>
      </header>

      <AdminOrderTabs />

      <AdminSearchBox onSearch={handleSearch} />

      <AdminOrderFilters />

      {isFetching && !isLoading && (
        <div className="mb-3 text-sm text-gray-500">
          در حال بروزرسانی اطلاعات...
        </div>
      )}

      <div className="mt-6">{content}</div>


    <div className="mt-8">
      <Pagination
        page={currentPage}
        setPage={setCurrentPage}
        totalPages={totalPages}
      />
    </div>

      <AdminReviewViewModal
        open={isReviewViewModalOpen}
        onClose={closeReviewModal}
        entity={reviewEntity}
        reviewData={reviewData}
      />
    </div>
  );
};

const SkeletonLoader = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse bg-white p-6 rounded-xl shadow-sm border border-gray-100"
      >
        <div className="flex justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-6 bg-gray-200 rounded w-1/6" />
        </div>

        <div className="space-y-3">
          <div className="h-4 bg-gray-100 rounded w-full" />
          <div className="h-4 bg-gray-100 rounded w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

export default AdminOrdersPage;
